import { createApi } from '@reduxjs/toolkit/query/react'
import type { ThreadDeal } from '@adthlete/shared-frontend'
import { baseQuery } from './baseQuery'

export interface DirectMessageThread {
  id: string
  subject: string | null
  lastMessageAt: string
  createdAt: string
  contact: {
    id: string
    name: string
    avatar: string | null
  }
  org: {
    id: number
    name: string
    logo: string | null
  } | null
  lastMessage: { body: string; isFromMe: boolean } | null
  unreadCount: number
}

export interface DirectMessage {
  id: string
  body: string
  createdAt: string
  isFromMe: boolean
  /** Who sent it, when that isn't you or the contact, e.g. your manager */
  byline?: string | null
  sender: {
    id: string
    name: string
    avatar: string | null
  }
}

export const messagesApi = createApi({
  reducerPath: 'messagesApi',
  baseQuery,
  tagTypes: ['Thread', 'ThreadMessages'],
  endpoints: (builder) => ({
    getMessageThreads: builder.query<{ threads: DirectMessageThread[] }, void>({
      query: () => '/messages',
      providesTags: ['Thread'],
    }),
    getThreadMessages: builder.query<
      {
        thread: {
          id: string
          subject: string | null
          contact: { id: string; name: string; avatar: string | null } | null
          org: { id: number; name: string; logo: string | null } | null
          /** Deals the brand offered from this conversation, newest first */
          deals: ThreadDeal[]
        }
        messages: DirectMessage[]
      },
      string
    >({
      query: (threadId) => `/messages/${threadId}/messages`,
      providesTags: (result, error, threadId) => [{ type: 'ThreadMessages', id: threadId }],
      onQueryStarted: async (threadId, { dispatch, queryFulfilled }) => {
        let clearedUnreadCount = 0

        const threadsPatch = dispatch(
          messagesApi.util.updateQueryData('getMessageThreads', undefined, (draft) => {
            const thread = draft.threads.find((t) => t.id === threadId)
            if (!thread || thread.unreadCount <= 0) return

            clearedUnreadCount = thread.unreadCount
            thread.unreadCount = 0
          }),
        )

        const unreadCountPatch = dispatch(
          messagesApi.util.updateQueryData('getUnreadCount', undefined, (draft) => {
            if (clearedUnreadCount <= 0) return
            draft.unreadCount = Math.max(0, draft.unreadCount - clearedUnreadCount)
          }),
        )

        try {
          await queryFulfilled
          dispatch(messagesApi.util.invalidateTags(['Thread']))
        } catch {
          threadsPatch.undo()
          unreadCountPatch.undo()
        }
      },
    }),
    sendMessageReply: builder.mutation<DirectMessage, { threadId: string; message: string }>({
      query: ({ threadId, message }) => ({
        url: `/messages/${threadId}/messages`,
        method: 'POST',
        body: { message },
      }),
      invalidatesTags: (result, error, { threadId }) => [
        'Thread',
        { type: 'ThreadMessages', id: threadId },
      ],
    }),
    getUnreadCount: builder.query<{ unreadCount: number }, void>({
      query: () => '/messages/unread-count',
      providesTags: ['Thread'],
    }),
  }),
})

export const {
  useGetMessageThreadsQuery,
  useGetThreadMessagesQuery,
  useSendMessageReplyMutation,
  useGetUnreadCountQuery,
} = messagesApi
